import {Box, TextField} from '@mui/material';
import React, {useContext, useEffect, useState} from 'react';
import {AccountContext, useAccountContext} from '../../../contexts/AccountContext';
import useAccountContacts from '../../../hooks/useAccountContacts';
import {ErrorPage} from '../../../pages/ErrorPage';
import {
  AutoField,
  FieldValue,
  Option,
  RadioGroupField,
} from '../../../components/AutoForm/AutoFieldNew';
import Loading from '../../../components/Loading/Loading';
import {
  ownershipRadioField,
  ownershipTrustRadioField,
  planningObjectiveField,
  planningObjectivePurposeField,
} from './goalFields';
import {GoalFormValues, GoalPartProps} from './utils';
import {
  setOwnershipRadio,
  populateOwnershipRadioButtons,
  setGoalOwner,
  setGoalType,
  updateGoalFormName,
  validateGoalReasons,
} from './GoalReasonsHelper';
import {useAutoForm} from '../../../components/AutoForm/useAutoForm';
import {UPDATES} from '../../../shared/UPDATES';

export default function GoalReasons(props: GoalPartProps) {
  const {setRequireValidation, setGoalFormName} = props;
  const {values, setFieldValue} = useAutoForm<GoalFormValues>();
  const [hasPlanningObjectivePurpose, setHasPlanningObjectivePurpose] = useState(false);
  const [planningObjectivePurpose, setPlanningObjectivePurpose] =
    useState<RadioGroupField>(planningObjectivePurposeField);
  const [ownershipOptions, setOwnershipOptions] = useState<Option<FieldValue>[]>([]);
  const {accountContactsLoading, accountContactsData, accountContactsError} =
    useAccountContacts();
  const {setIsFullGoalSteps} = useContext(AccountContext);
  const {account} = useAccountContext();

  useEffect(() => {
    populateOwnershipRadioButtons({
      account,
      ownershipRadioField,
      setOwnershipOptions,
    });
  }, [account, ownershipRadioField]);

  useEffect(() => {
    setOwnershipRadio({setFieldValue, values, account});
  }, [values, account]);

  useEffect(() => {
    validateGoalReasons({
      values,
      hasPlanningObjectivePurpose,
      setRequireValidation,
    });
  }, [setRequireValidation, values, hasPlanningObjectivePurpose]);

  useEffect(() => {
    setGoalOwner({ownershipRadio: values.ownershipRadio, setFieldValue, account});
  }, [values.ownershipRadio, account]);

  useEffect(() => {
    setGoalType({
      setHasPlanningObjectivePurpose,
      setFieldValue,
      values,
      setIsFullGoalSteps,
      setPlanningObjectivePurpose,
      planningObjectivePurposeField,
    });
  }, [values.planningObjective]);

  useEffect(() => {
    updateGoalFormName({setFieldValue, setGoalFormName, values});
  }, [JSON.stringify(values)]);

  if (accountContactsLoading) {
    return <Loading />;
  }

  if (accountContactsError) {
    return (
      <ErrorPage
        code="Server Error"
        text="We were unable to retrieve the account information."
      />
    );
  }

  return (
    <Box mt={3} ml={4} width="100%" height="100%">
      <Box display="flex" flexDirection="row">
        <Box width="40%" mr={5}>
          {account?.isTrust ? (
            <AutoField key={values.ownershipRadio} field={ownershipTrustRadioField} />
          ) : (
            <AutoField
              key={values.ownershipRadio}
              field={{
                ...ownershipRadioField,
                options: ownershipOptions,
              }}
            />
          )}
          <Box mt={3} />
        </Box>
        <Box width="50%">
          <TextField
            style={{
              width: '100%',
              height: '10rem',
              minHeight: '10rem',
            }}
            id="goal-text"
            label="Goal description in client's own words (required)"
            placeholder="Describe your goal..."
            required
            multiline
            variant="outlined"
            rows={7}
            value={values.description}
            onChange={({target}) => {
              setFieldValue(UPDATES._1113, 'description', target.value);
            }}
          />
        </Box>
      </Box>
      <Box display="flex" flexDirection="row" mt={2}>
        <Box width="40%" mr={5}>
          <AutoField field={planningObjectiveField} />
        </Box>
        {hasPlanningObjectivePurpose && (
          <Box width="50%" display="flex" flexDirection="row">
            <div>
              <AutoField field={planningObjectivePurpose} />
            </div>
          </Box>
        )}
      </Box>
    </Box>
  );
}
